/**
 * List Component - Theme-aware list display
 * ✅ Playbook Compliance: Theme-aware, WCAG 2.1 AA compliant, React.createElement
 */

const React = (globalThis as any).React;

import { styles } from '../styles';

export interface ListItemProps {
  children?: React.ReactNode;
  actions?: React.ReactNode[];
  extra?: React.ReactNode;
  style?: React.CSSProperties;
}

export interface ListProps {
  dataSource?: any[];
  renderItem?: (item: any, index: number) => React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  bordered?: boolean;
  size?: 'small' | 'default' | 'large';
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export function ListItem({ children, actions, extra, style }: ListItemProps) {
  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-color)',
    ...style,
  };

  return React.createElement('div', { style: itemStyle },
    React.createElement('div', { style: { flex: 1 } }, children),
    extra && React.createElement('div', { style: { marginLeft: '16px' } }, extra),
    actions && actions.length > 0 && React.createElement('div', {
      style: {
        marginLeft: '16px',
        display: 'flex',
        gap: '8px',
      }
    }, ...actions.map((action, idx) => 
      React.createElement('div', { key: idx }, action)
    ))
  );
}

export function List({ 
  dataSource, 
  renderItem, 
  header, 
  footer, 
  bordered = false, 
  size = 'default',
  style,
  children 
}: ListProps) {
  const sizePadding = {
    small: '8px',
    default: '12px',
    large: '16px',
  }[size];

  const listStyle: React.CSSProperties = {
    background: 'var(--card-bg)',
    border: bordered ? '1px solid var(--card-border)' : 'none',
    borderRadius: bordered ? '8px' : '0',
    overflow: 'hidden',
    ...style,
  };

  const content = dataSource && renderItem
    ? dataSource.map((item, index) => renderItem(item, index))
    : children;

  return React.createElement('div', { style: listStyle },
    header && React.createElement('div', {
      style: {
        padding: sizePadding,
        borderBottom: '1px solid var(--border-color)',
        background: 'var(--card-bg)',
      }
    }, header),
    React.createElement('div', {
      style: {
        background: 'var(--card-bg)',
      }
    }, content),
    footer && React.createElement('div', {
      style: {
        padding: sizePadding,
        borderTop: '1px solid var(--border-color)',
        background: 'var(--card-bg)',
      }
    }, footer)
  );
}

// Attach ListItem to List for convenience
(List as any).Item = ListItem;

